import { Box, CardContent, Stack, Typography } from "@mui/material";
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { ZodSchema } from "zod";
import CardWithHeader from "../components/cards/CardWithHeader";
import { StyledFormContainer } from "../components/FormContainer";
import {
  ButtonSelectionInput,
  PasswordInput,
  SelectFormInput,
  TagSelectInput,
  TextInput,
} from "../components/inputs";
import CheckboxesInput from "../components/inputs/CheckboxesInput";
import DateInput from "../components/inputs/DateInput";
import HeightInput from "../components/inputs/HeightInput";
import MonthInput from "../components/inputs/MonthInput";
import { SelectFormInputOption } from "../components/inputs/SelectFormInput";
import WeightInput from "../components/inputs/WeightInput";
import SubmitButton from "../components/SubmitButton";
import { useApp } from "../contexts/AppContext";
import { useFormContext } from "../contexts/FormContext";
import { colors, fontSizes } from "../theme";
import { MeasuringSystem } from "../types";
import { scrollToElement, toKebabCase } from "../utils";
import { useApi } from "../contexts/ApiContext";
type FieldType =
  | "button-selection"
  | "select"
  | "password"
  | "tag-select"
  | "text"
  | "number"
  | "float"
  | "integer"
  | "date"
  | "height"
  | "weight"
  | "checkboxes"
  | "month";

export interface FormSectionRow {
  fields: FieldConfig[];
}

export interface FormSection {
  label: string;
  subsections?: FormSection[];
  rows?: FormSectionRow[];
}

export interface FieldConfig {
  name: string;
  type: FieldType;
  label?: string;
  placeholder?: string | null;
  required: boolean;
  options?: SelectFormInputOption[];
  multiline?: boolean;
  adornment?: string;
  parseValue?: (value: any) => any;
  parseKey?: (key: string) => string;
  caption?: string;
}

export type ConfigType = { right: FormSection[]; left?: FormSection[] };

export interface FormProps {
  langCategory: string;
  initialValues: Record<string, any>;
  config: ConfigType;
  onSubmit: (data: Record<string, any>) => void;
  schema: ZodSchema<any>;
  onChange: (data: Record<string, any>) => void;
  submitText?: string;
  system: MeasuringSystem;
  title?: string;
  uploads?: any;
  data: any;
}

const Form: React.FC<FormProps> = ({
  onChange,
  langCategory,
  config,
  onSubmit,
  schema,
  submitText,
  title,
  system = "metric",
  uploads = [],
  data,
}) => {
  // const [data, setData] = React.useState(initialValues);
  const [errors, setErrors] = React.useState<any>({});
  const [validateOnChanges, setValidateOnChange] = React.useState(false);

  const { updateActions, translate } = useApp();
  const { updateCurrentSection } = useFormContext();
  const { isSubmitting } = useApi();

  const headerRefs = useRef<(HTMLElement | null)[]>([]);
  const observer = useRef<IntersectionObserver | null>(null);

  const handleChange = (field: FieldConfig, value: string) => {
    onChange({
      ...data,
      [field.name]: value,
    });
  };

  useEffect(() => {
    updateActions([
      <SubmitButton key="submit" onClick={onPreSubmit}>
        {translate("general", "form.buttons.save")}
      </SubmitButton>,
    ]);

    return () => {
      updateActions([]);
    };
  }, [data, uploads, isSubmitting]);

  const validateForm = useCallback(
    (scrollToError: boolean = false) => {
      const result = schema.safeParse(data);
      console.info("validateForm data", data);
      console.info("validate", result);
      if (!result.success) {
        const errorDetails = result.error.flatten().fieldErrors;
        console.info("errors", errorDetails);
        setErrors(errorDetails as any);

        if (scrollToError) {
          const firstError = Object.keys(errorDetails)[0];
          const el = document.getElementById(firstError);
          if (el) {
            scrollToElement(el);
          }
        }
      } else {
        setErrors({});
        return true;
      }
      return false;
    },
    [data, uploads]
  );

  const onPreSubmit = useCallback(() => {
    if (validateForm(true)) {
      onSubmit(data);
    }
    setValidateOnChange(true);
  }, [onSubmit, data, uploads]);

  const renderField = (field: FieldConfig) => {
    const translatedLabel = translate(langCategory, field.label);
    const translatedCaption = field.caption
      ? translate(langCategory, field.caption)
      : "";
    const translatedPlaceholder = field.placeholder
      ? translate(langCategory, field.placeholder)
      : "";

    console.info("placeholder", translatedPlaceholder);

    const translatedError = errors[field.name]
      ? translate(langCategory, `form.${errors[field.name]}`)
      : "";

    const commonProps = {
      key: field.name,
      name: field.name,
      label: translatedLabel,
      value: data[field.name],
      onChange: (value: any) => handleChange(field, value),
      required: field.required,
      error: !!errors[field.name],
      helperText: translatedError,
      multiline: field.multiline,
      adornment: field.adornment,
      placeholder: translatedPlaceholder,
      caption: translatedCaption,
    };
    switch (field.type) {
      case "number":
      case "float":
      case "integer":
        return (
          <TextInput
            id={field.name}
            type={field.type}
            {...commonProps}
            key={commonProps.key}
          />
        );
      case "text":
        return (
          <TextInput id={field.name} {...commonProps} key={commonProps.key} />
        );
      case "height":
        return (
          <HeightInput
            id={field.name}
            isRequired={field.required}
            system={system}
            {...commonProps}
            key={commonProps.key}
          />
        );
      case "weight":
        return (
          <WeightInput
            id={field.name}
            isRequired={field.required}
            system={system}
            {...commonProps}
            key={commonProps.key}
          />
        );
      case "password":
        return <PasswordInput {...commonProps} />;
      case "select":
        return (
          <SelectFormInput
            id={field.name}
            {...commonProps}
            options={field.options ?? []}
            key={commonProps.key}
          />
        );
      case "button-selection":
        return (
          <ButtonSelectionInput
            id={field.name}
            {...commonProps}
            options={field.options ?? []}
            key={commonProps.key}
          />
        );
      case "tag-select":
        return (
          <TagSelectInput
            id={field.name}
            {...commonProps}
            options={field.options ?? []}
            key={commonProps.key}
          />
        );
      case "checkboxes":
        return (
          <CheckboxesInput
            id={field.name}
            {...commonProps}
            options={field.options ?? []}
            key={commonProps.key}
          />
        );
      case "date":
        return (
          <DateInput id={field.name} {...commonProps} key={commonProps.key} />
        );
      case "month":
        return (
          <MonthInput id={field.name} {...commonProps} key={commonProps.key} />
        );
      default:
        console.error(`Unknown field type: ${field.type}`);
    }
  };

  const initObserver = useCallback(() => {
    console.log("initObserver", observer.current);

    if (observer.current || !headerRefs.current.length) return;

    observer.current = new IntersectionObserver(
      (entries) => {
        console.log("entries", entries);
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = headerRefs.current.indexOf(
              entry.target as HTMLElement
            );
            if (index >= 0) {
              // Step 3: Call function on header scroll
              handleHeaderScroll(index);
            }
          }
        });
      },
      { threshold: 0.5 }
    );

    headerRefs.current.forEach((header) => {
      console.log("header", header);
      if (header) observer.current?.observe(header);
    });
  }, [config.right, headerRefs.current, observer.current]);

  const handleHeaderScroll = (index: number) => {
    console.log(`Header scrolled into view: ${index}`);
    updateCurrentSection(index);
  };

  useEffect(() => {
    setTimeout(() => {
      initObserver();
    }, 1000);
    return () => {
      observer.current?.disconnect();
    };
  }, [config.right]);

  return (
    <StyledFormContainer>
      {title && <Typography variant="title">{title}</Typography>}
      <form>
        <Stack gap={2}>
          {config.right.map((section, index) => (
            <Box
              key={index}
              ref={(el: HTMLDivElement | null) => {
                if (el) headerRefs.current[index] = el;
              }}
            >
              <CardWithHeader
                key={index}
                id={toKebabCase(section.label).toLowerCase()}
                headerColor="inherit"
                fontSize={fontSizes.lg}
                fontWeight={800}
                title={translate(langCategory, section.label)}
              >
                <Stack key={section.label} gap={2}>
                  {section.subsections ? (
                    section.subsections?.map((subsection, index) => (
                      <CardWithHeader
                        fontWeight={700}
                        fontSize={fontSizes.sm}
                        backgroundColor={colors.stone[50]}
                        id={toKebabCase(subsection.label).toLowerCase()}
                        headerColor="inherit"
                        title={translate(langCategory, subsection.label)}
                      >
                        <Stack gap={2}>
                          {subsection.rows?.map((row, index) => (
                            <Box
                              gap={1}
                              key={index}
                              justifyContent={"space-between"}
                              display="flex"
                              flexDirection="row"
                            >
                              {row.fields.map((field, index) =>
                                renderField(field)
                              )}
                            </Box>
                          ))}
                        </Stack>
                      </CardWithHeader>
                    ))
                  ) : (
                    <CardContent key={section.label} sx={{ padding: 0 }}>
                      <Stack gap={2}>
                        {section.rows?.map((row, index) => (
                          <Box
                            gap={1}
                            key={index}
                            justifyContent={"space-between"}
                            display="flex"
                            flexDirection="row"
                          >
                            {row.fields.map((field, index) =>
                              renderField(field)
                            )}
                          </Box>
                        ))}
                      </Stack>
                    </CardContent>
                  )}
                </Stack>
              </CardWithHeader>
            </Box>
          ))}
        </Stack>
      </form>
    </StyledFormContainer>
  );
};

export default Form;
